import moment from "moment";

import { SortingState } from "@tanstack/react-table";
import { SortColumn, SortDirection as RdgSortDirection } from "react-data-grid";
import type {
  ActivityConfig,
  DropdownId,
  DetailTabData,
  MentionEntity,
  RichTextFormat,
  TaskSort,
  CreateTaskPayload,
  RelatedType
} from "./types";
import {
  DefaultOptions,
  CreatedAtOptions,
  DueDateFilterOptions,
  DueDateOptions,
  TaskSubject,
  SortDirection,
  TaskActivity,
  TaskSubjectPayload
} from "./types";

export enum TaskTab {
  DETAILS = "Details",
  ACTIVITY = "Activity"
}
export enum ActivityTabs {
  ALL = "All",
  MENTIONS = "Mentions"
}
export const DefaultOptionsMap: Record<DefaultOptions, string> = {
  [DefaultOptions.ASSIGNEE]: "No assignee",
  [DefaultOptions.RELATED_OBJECT]: "No related object"
};
const DueDateFilterLabel: Record<DueDateFilterOptions, string> = {
  [DueDateFilterOptions.NONE]: "Don't filter",
  [DueDateFilterOptions.TODAY]: "Today",
  [DueDateFilterOptions.OVERDUE]: "Overdue",
  [DueDateFilterOptions.THIS_WEEK]: "This week",
  [DueDateFilterOptions.CUSTOM]: "Pick a date range"
};
const CreatedAtLabels: Record<CreatedAtOptions, string> = {
  [CreatedAtOptions.NONE]: "Don't filter",
  [CreatedAtOptions.TODAY]: "Today",
  [CreatedAtOptions.THIS_WEEK]: "This week",
  [CreatedAtOptions.CUSTOM]: "Pick a date range"
};
export const DueDateLabels: Record<DueDateOptions, string> = {
  [DueDateOptions.NONE]: "No due date",
  [DueDateOptions.TODAY]: "Today",
  [DueDateOptions.TOMORROW]: "Tomorrow",
  [DueDateOptions.NEXT_WEEK]: "Next week",
  [DueDateOptions.CUSTOM]: "Pick a date"
};
export const columnName = {
  title: "Task",
  related: "Related to",
  due_date: "Due date",
  assignees: "Assigned to",
  createdAt: "Created at",
  updatedAt: "Updated at"
};
export const DropDowns = ["assignee", "due", "related", "datePicker"] as const;
export const DropdownLabels: Record<DropdownId, string> = {
  assignee: "Assignee",
  due: "Due date",
  related: "Related to",
  datePicker: "Pick a date"
};
export const timeLineSection = {
  updated_at: "Updated at",
  completed_at: "Completed at",
  completed_by: "Completed by",
  created_at: "Created at",
  created_by: "Created by",
  link: "Link"
};
export const TaskOptions = {
  filters: "Filter",
  filterAssignee: "Filter assignee",
  create: "Create task",
  view: "Review task",
  drawer: "Drawer",
  delete: "Delete task"
};
export const dueLabel: Record<number, string> = {
  0: "Due today",
  1: "Due tomorrow"
};

export const RelatedMapping: Record<TaskSubject, RelatedType> = {
  [TaskSubject.BOOM_SCREEN_APPLICATIONS]: "Rental application",
  [TaskSubject.CRM_LEADS]: "Lead"
};
export const RelatedPayloadMapping: Record<TaskSubjectPayload, RelatedType> = {
  [TaskSubjectPayload.BOOM_SCREEN_APPLICATIONS]: "Rental application",
  [TaskSubjectPayload.CRM_LEADS]: "Lead"
};

const CreatedAtIconMapping: Record<CreatedAtOptions, string> = {
  [CreatedAtOptions.NONE]: "CalendarMinus02Icon",
  [CreatedAtOptions.TODAY]: "Calendar01Icon",
  [CreatedAtOptions.THIS_WEEK]: "CalendarDownload02Icon",
  [CreatedAtOptions.CUSTOM]: "Calendar03Icon"
};
const DueFilterIconMapping: Record<DueDateFilterOptions, string> = {
  [DueDateFilterOptions.NONE]: "CalendarMinus02Icon",
  [DueDateFilterOptions.OVERDUE]: "CalendarRemove02Icon",
  [DueDateFilterOptions.TODAY]: "Calendar01Icon",
  [DueDateFilterOptions.THIS_WEEK]: "CalendarDownload02Icon",
  [DueDateFilterOptions.CUSTOM]: "Calendar03Icon"
};

interface ButtonOptionConfig {
  label: string;
  icon: string;
  textColor?: string;
}

const CreatedAtConfigMap: Record<CreatedAtOptions, ButtonOptionConfig> = {
  [CreatedAtOptions.NONE]: {
    label: CreatedAtLabels[CreatedAtOptions.NONE],
    icon: CreatedAtIconMapping[CreatedAtOptions.NONE]
  },
  [CreatedAtOptions.TODAY]: {
    label: CreatedAtLabels[CreatedAtOptions.TODAY],
    icon: CreatedAtIconMapping[CreatedAtOptions.TODAY]
  },
  [CreatedAtOptions.THIS_WEEK]: {
    label: CreatedAtLabels[CreatedAtOptions.THIS_WEEK],
    icon: CreatedAtIconMapping[CreatedAtOptions.THIS_WEEK]
  },
  [CreatedAtOptions.CUSTOM]: {
    label: CreatedAtLabels[CreatedAtOptions.CUSTOM],
    icon: CreatedAtIconMapping[CreatedAtOptions.CUSTOM]
  }
};

const DueDateFilterConfigMap: Record<DueDateFilterOptions, ButtonOptionConfig> =
  {
    [DueDateFilterOptions.NONE]: {
      label: DueDateFilterLabel[DueDateFilterOptions.NONE],
      icon: DueFilterIconMapping[DueDateFilterOptions.NONE]
    },
    [DueDateFilterOptions.OVERDUE]: {
      label: DueDateFilterLabel[DueDateFilterOptions.OVERDUE],
      icon: DueFilterIconMapping[DueDateFilterOptions.OVERDUE],
      textColor: "!text-danger-500"
    },
    [DueDateFilterOptions.TODAY]: {
      label: DueDateFilterLabel[DueDateFilterOptions.TODAY],
      icon: DueFilterIconMapping[DueDateFilterOptions.TODAY]
    },
    [DueDateFilterOptions.THIS_WEEK]: {
      label: DueDateFilterLabel[DueDateFilterOptions.THIS_WEEK],
      icon: DueFilterIconMapping[DueDateFilterOptions.THIS_WEEK]
    },
    [DueDateFilterOptions.CUSTOM]: {
      label: DueDateFilterLabel[DueDateFilterOptions.CUSTOM],
      icon: DueFilterIconMapping[DueDateFilterOptions.CUSTOM]
    }
  };

const generateButtonConfig = <T extends string>(
  keys: T[],
  configMap: Record<T, ButtonOptionConfig>
) =>
  keys.map((key) => ({
    key,
    ...configMap[key]
  }));

export const CreatedAtButtonConfig = generateButtonConfig<CreatedAtOptions>(
  [
    CreatedAtOptions.NONE,
    CreatedAtOptions.TODAY,
    CreatedAtOptions.THIS_WEEK,
    CreatedAtOptions.CUSTOM
  ],
  CreatedAtConfigMap
);
const dateMap = {
  PAST: "1970-01-01T00:00:00Z",
  YESTERDAY: moment.utc().subtract(1, "day").startOf("day").toISOString(),
  TODAY: moment.utc().startOf("day").toISOString(),
  WEEK_START: moment.utc().startOf("week").toISOString(),
  WEEK_END: moment.utc().endOf("week").toISOString()
};
export const DueFilterButtonConfig = generateButtonConfig<DueDateFilterOptions>(
  [
    DueDateFilterOptions.NONE,
    DueDateFilterOptions.OVERDUE,
    DueDateFilterOptions.TODAY,
    DueDateFilterOptions.THIS_WEEK,
    DueDateFilterOptions.CUSTOM
  ],
  DueDateFilterConfigMap
);
export const DueFilterMapping = {
  [DueDateFilterOptions.OVERDUE]: {
    from: dateMap.PAST,
    to: dateMap.YESTERDAY
  },
  [DueDateFilterOptions.TODAY]: {
    from: dateMap.TODAY,
    to: dateMap.TODAY
  },
  [DueDateFilterOptions.THIS_WEEK]: {
    from: dateMap.WEEK_START,
    to: dateMap.WEEK_END
  }
};

export const CreatedAtFilterMapping = {
  [CreatedAtOptions.TODAY]: {
    from: dateMap.TODAY,
    to: dateMap.TODAY
  },
  [CreatedAtOptions.THIS_WEEK]: {
    from: dateMap.WEEK_START,
    to: dateMap.WEEK_END
  }
};

export const NO_ASSIGNEE_ID = 0;

export const DUE_COLOR: Record<string, string[]> = {
  "text-danger-600": ["day ago", "days ago"],
  "text-warning-600": ["tomorrow", "today"]
};
const SORT_COLUMN_MAP: Record<
  string,
  "due_date" | "created_at" | "updated_at" | "status" | undefined
> = {
  dueLabel: "due_date",
  createdAt: "created_at",
  updatedAt: "updated_at",
  status: "status"
};

export const toRdgSort = (sorting: SortingState): SortColumn[] => {
  return sorting.map((s) => ({
    columnKey: s.id,
    direction: (s.desc
      ? SortDirection.DESCENDING.toUpperCase()
      : SortDirection.ASCENDING.toUpperCase()) as RdgSortDirection
  }));
};

export const toTanStackSort = (rdgSort: SortColumn[]): SortingState => {
  return rdgSort.map((s) => ({
    id: s.columnKey,
    desc: s.direction === SortDirection.DESCENDING.toUpperCase()
  }));
};

export const mapTableSortingToApiSorts = (
  sorting: SortingState
): TaskSort[] => {
  const mapped = sorting
    .map(({ id, desc }) => {
      const apiField = SORT_COLUMN_MAP[id];
      if (!apiField) return null;
      return {
        [apiField]: desc ? SortDirection.DESCENDING : SortDirection.ASCENDING
      } as TaskSort;
    })
    .filter(Boolean) as TaskSort[];

  return mapped;
};

export const DATE_FORMATS = {
  DATE: "MMM D, YYYY",
  TIME: "h:mm A"
};

export const formatTaskCreatedDate = (isoDate: string) => {
  const createdDate = moment(isoDate);
  return {
    label: createdDate.format(DATE_FORMATS.DATE),
    time: createdDate.format(DATE_FORMATS.TIME)
  };
};

export const getDueDateLabel = (dueDate: string | null | undefined): string => {
  if (!dueDate) return DueDateLabels[DueDateOptions.NONE];
  if (moment.utc(dueDate).isSame(moment.utc().startOf("day")))
    return DueDateLabels[DueDateOptions.TODAY];
  if (moment.utc(dueDate).isSame(moment.utc().add(1, "day").startOf("day")))
    return DueDateLabels[DueDateOptions.TOMORROW];
  if (moment.utc(dueDate).isSame(moment.utc().add(1, "week").startOf("day")))
    return DueDateLabels[DueDateOptions.NEXT_WEEK];
  return moment.utc(dueDate).format(DATE_FORMATS.DATE);
};
const ActivityLabel = {
  [TaskActivity.CREATE_TASK]: "Task created",
  [TaskActivity.CHANGE_TASK_TITLE]: "Task title changed",
  [TaskActivity.CHANGE_TASK_ASSIGNEE]: "Task assignee changed",
  [TaskActivity.CHANGE_TASK_DESCRIPTION]: "Task description changed",
  [TaskActivity.CHANGE_TASK_DUE]: "Task due date changed",
  [TaskActivity.CHANGE_TASK_SUBJECT]: "Task related object changed",
  [TaskActivity.COMPLETE_TASK]: "Task coompleted",
  [TaskActivity.REOPEN_TASK]: "Task reopened"
};

const ActivityIconMapping: Record<TaskActivity, string> = {
  [TaskActivity.CREATE_TASK]: "AddCircleIcon",
  [TaskActivity.CHANGE_TASK_TITLE]: "File02Icon",
  [TaskActivity.CHANGE_TASK_DESCRIPTION]: "File02Icon",
  [TaskActivity.CHANGE_TASK_DUE]: "Calendar04Icon",
  [TaskActivity.CHANGE_TASK_SUBJECT]: "File02Icon",
  [TaskActivity.CHANGE_TASK_ASSIGNEE]: "UserCircleIcon",
  [TaskActivity.COMPLETE_TASK]: "CheckmarkCircle01Icon",
  [TaskActivity.REOPEN_TASK]: "CircleIcon",
  [TaskActivity.NOTE]: "Note02Icon"
};

export const ActivityConfigMap: Record<TaskActivity, ActivityConfig> = {
  [TaskActivity.CREATE_TASK]: {
    label: ActivityLabel[TaskActivity.CREATE_TASK],
    iconName: ActivityIconMapping[TaskActivity.CREATE_TASK]
  },
  [TaskActivity.CHANGE_TASK_TITLE]: {
    label: ActivityLabel[TaskActivity.CHANGE_TASK_TITLE],
    iconName: ActivityIconMapping[TaskActivity.CHANGE_TASK_TITLE]
  },
  [TaskActivity.CHANGE_TASK_DESCRIPTION]: {
    label: ActivityLabel[TaskActivity.CHANGE_TASK_DESCRIPTION],
    iconName: ActivityIconMapping[TaskActivity.CHANGE_TASK_DESCRIPTION]
  },
  [TaskActivity.CHANGE_TASK_DUE]: {
    label: ActivityLabel[TaskActivity.CHANGE_TASK_DUE],
    iconName: ActivityIconMapping[TaskActivity.CHANGE_TASK_DUE]
  },
  [TaskActivity.CHANGE_TASK_SUBJECT]: {
    label: ActivityLabel[TaskActivity.CHANGE_TASK_SUBJECT],
    iconName: ActivityIconMapping[TaskActivity.CHANGE_TASK_SUBJECT]
  },
  [TaskActivity.CHANGE_TASK_ASSIGNEE]: {
    label: ActivityLabel[TaskActivity.CHANGE_TASK_ASSIGNEE],
    iconName: ActivityIconMapping[TaskActivity.CHANGE_TASK_ASSIGNEE]
  },
  [TaskActivity.COMPLETE_TASK]: {
    label: ActivityLabel[TaskActivity.COMPLETE_TASK],
    iconName: ActivityIconMapping[TaskActivity.COMPLETE_TASK]
  },
  [TaskActivity.REOPEN_TASK]: {
    label: ActivityLabel[TaskActivity.REOPEN_TASK],
    iconName: ActivityIconMapping[TaskActivity.REOPEN_TASK]
  },
  [TaskActivity.NOTE]: {
    label: "left a note",
    iconName: ActivityIconMapping[TaskActivity.NOTE]
  }
};

export const buildTaskPayload = (
  currentTitle: RichTextFormat,
  currentDetailData: DetailTabData | null,
  entities: MentionEntity[]
): CreateTaskPayload => ({
  title: {
    document_text: currentTitle.document_text,
    document_html: currentTitle.document_html,
    entities
  },
  description: currentDetailData?.description || {
    document_text: "",
    document_html: "<p></p>",
    entities: []
  },
  ...(currentDetailData?.due_date && { due_date: currentDetailData.due_date }),
  ...(currentDetailData?.subject && {
    related_to: {
      subject_type:
        currentDetailData.subject.type === "boom_screen_applications"
          ? TaskSubjectPayload.BOOM_SCREEN_APPLICATIONS
          : TaskSubjectPayload.CRM_LEADS,
      subject_id: currentDetailData.subject.id
    }
  }),
  ...(currentDetailData?.assignees && {
    assignee_ids: currentDetailData.assignees.map(({ id }) => id)
  })
});
export const noOffsetDate = (date: moment.MomentInput) => {
  if (typeof date === "string" && date.includes("T"))
    return moment.utc(date).startOf("day").format("YYYY-MM-DD");
  return moment(date).startOf("day").format("YYYY-MM-DD");
};
export const mentionDescription = (
  mentionEmailedUsers: string | null,
  isMailed?: boolean,
  amount?: number
) => {
  if (!amount || !mentionEmailedUsers) return null;

  if (!isMailed)
    return `${mentionEmailedUsers} will be emailed about the mention`;

  const verb = amount === 1 ? "was" : "were";
  return `${mentionEmailedUsers} ${verb} emailed about the mention`;
};
